/**
 * phone-input.jsx — TREATX-UAT-RELEASE-FIX-SEQUENCE-01 / Phase 2
 *
 * Reusable phone input combining a country-dial-code dropdown with a
 * digits-only national-number field. Always emits an E.164 string
 * (e.g. "+905551234567") via `onChange`, so call sites can store the
 * canonical form without further processing.
 *
 * Hydrates correctly from an existing E.164 string via `value` (the
 * country selector auto-matches and the national portion is split out).
 *
 * Surfaces inline validation errors. Does NOT block save by itself — the
 * call site should treat `error` as advisory and aggregate with its own
 * form-level state. The companion validator `validateE164` from
 * `utils/phoneNormalize.js` should be used at save time.
 *
 * Visual style mirrors existing form controls in the project (no new
 * design language introduced).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DIAL_CODES,
  composeE164,
  parseE164,
  normalizeNationalInput,
  validateE164,
} from '../../utils/phoneNormalize';

const DEFAULT_ISO = 'TR';

export function PhoneInput({
  value,                 // E.164 string ("+905551234567") or empty
  onChange,              // (e164: string) => void
  defaultCountry = DEFAULT_ISO,
  disabled = false,
  required = false,
  showError = true,
  className = '',
  testId = 'phone-input',
  label,
  placeholder,
}) {
  const { t } = useTranslation();
  if (placeholder === undefined) placeholder = t('provider.onboarding.phonePlaceholder');
  // Derive initial state from incoming `value`; fall back to defaultCountry.
  const initialParsed = useMemo(() => parseE164(value || ''), [value]);
  const [iso, setIso] = useState(
    initialParsed?.iso || defaultCountry,
  );
  const [national, setNational] = useState(initialParsed?.national || '');

  // Keep state in sync when the parent swaps `value` (e.g. profile load).
  // Controlled prop-sync: bails out if the parsed result already matches.
  useEffect(() => {
    if (!value) return;
    const p = parseE164(value);
    if (!p) return;
    if (p.iso && p.iso !== iso) setIso(p.iso);
    if (p.national !== national) setNational(p.national);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const e164 = composeE164(iso, national);
  const validation = validateE164(e164);

  const emit = (nextIso, nextNational) => {
    const out = composeE164(nextIso, nextNational);
    onChange?.(out);
  };

  const handleCountryChange = (e) => {
    const nextIso = e.target.value;
    setIso(nextIso);
    emit(nextIso, national);
  };

  const handleNationalChange = (e) => {
    const clean = normalizeNationalInput(e.target.value);
    setNational(clean);
    emit(iso, clean);
  };

  const country = DIAL_CODES.find((c) => c.iso === iso) || DIAL_CODES[0];
  const showInlineError = showError && national && !validation.valid;
  const showRequiredHint = showError && required && !national;

  return (
    <div className={`flex flex-col gap-1 ${className}`} data-testid={testId}>
      {label && (
        <label className="text-[11px] text-slate-600">{label}{required && <span className="text-rose-600"> *</span>}</label>
      )}
      <div className={`flex items-stretch border rounded ${
        showInlineError ? 'border-rose-400 bg-rose-50/30' : 'border-slate-300'
      }`}>
        <select
          value={iso}
          onChange={handleCountryChange}
          disabled={disabled}
          aria-label="Country dial code"
          data-testid={`${testId}-country`}
          className="bg-white px-2 py-1.5 text-[12.5px] border-r border-slate-300 rounded-l outline-none focus:ring-1 focus:ring-slate-400"
        >
          {DIAL_CODES.map((c) => (
            <option key={c.iso} value={c.iso}>
              {c.iso} +{c.dial}
            </option>
          ))}
        </select>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          disabled={disabled}
          value={national}
          onChange={handleNationalChange}
          placeholder={placeholder}
          data-testid={`${testId}-national`}
          className="flex-1 px-2 py-1.5 text-[12.5px] rounded-r outline-none focus:ring-1 focus:ring-slate-400"
        />
      </div>
      {(showInlineError || showRequiredHint) && (
        <span className="text-[10.5px] text-rose-600" data-testid={`${testId}-error`}>
          {showRequiredHint ? t('provider.phoneInput.required') : validation.error}
        </span>
      )}
      {/* Hidden field for native HTML form submission with the canonical
          E.164 value — gives traditional <form action>/AJAX callers the
          stored form without any extra work. */}
      <input type="hidden" name={testId} value={e164} readOnly />
    </div>
  );
}

export default PhoneInput;
